import React, { useEffect, useState, useCallback } from 'react';
import { useGuides } from '../../hooks/useEducationHub';
import { Guide } from '../../types/EducationHub';
import s from '../../styles/AdminGuides.module.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  title: '', description: '', url: '', category: '',
  slug: '', excerpt: '', cover_image_url: '', isPublished: false,
};

// ── Main component ────────────────────────────────────────────────────────────

const AdminGuides: React.FC = () => {
  const { fetchAdminGuides, createGuide, updateGuide, deleteGuide } = useGuides();

  const [guides, setGuides] = useState<Guide[]>([]);
  const [loadingGuides, setLoadingGuides] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoadingGuides(true);
    try {
      const list = await fetchAdminGuides();
      setGuides(list);
    } finally {
      setLoadingGuides(false);
    }
  }, [fetchAdminGuides]);

  useEffect(() => { reload(); }, [reload]);

  // Categories already in use, for the datalist — the guides meta/categories endpoint
  // returns raw category rows rather than names, so derive suggestions from loaded guides instead.
  const categoryOptions = Array.from(new Set(guides.map(g => g.category).filter(Boolean)));

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleEdit = (guide: Guide) => {
    setEditingId(guide._id);
    setForm({
      title: guide.title ?? '',
      description: guide.description ?? '',
      url: guide.url ?? '',
      category: guide.category ?? '',
      slug: guide.slug ?? '',
      excerpt: guide.excerpt ?? '',
      cover_image_url: guide.coverImageUrl ?? '',
      isPublished: guide.isPublished,
    });
    setFormError('');
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.url.trim() || !form.category.trim()) {
      setFormError('Title, description, content URL, and category are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        url: form.url.trim(),
        category: form.category.trim(),
        slug: form.slug.trim() || undefined,
        excerpt: form.excerpt.trim() || undefined,
        cover_image_url: form.cover_image_url.trim() || undefined,
        isPublished: form.isPublished,
      };
      if (editingId !== null) {
        await updateGuide(editingId, payload);
      } else {
        await createGuide(payload);
      }
      handleCancel();
      await reload();
    } catch {
      setFormError('Failed to save guide. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this guide? Any user progress tracked against it will be removed too.')) return;
    await deleteGuide(id);
    await reload();
  };

  const handleTogglePublished = async (guide: Guide) => {
    await updateGuide(guide._id, { url: guide.url || '', isPublished: !guide.isPublished });
    await reload();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={s.page}>
      <h2 className={s.pageTitle}>📖 Guides Management</h2>

      {/* ── Form ───────────────────────────────────────────────────────────── */}
      <section className={s.panel}>
        <h3 className={s.panelTitle}>{editingId !== null ? 'Edit Guide' : 'Add Guide'}</h3>
        <div className={s.formGrid}>
          <label className={s.formLabel}>
            Title *
            <input className={s.input} placeholder="Guide title" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </label>
          <label className={s.formLabel}>
            Category *
            <input className={s.input} placeholder="e.g. Getting Started" value={form.category}
              list="guide-categories"
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
            <datalist id="guide-categories">
              {categoryOptions.map(c => <option key={c} value={c} />)}
            </datalist>
          </label>
          <label className={s.formLabel}>
            Slug
            <input className={s.input} placeholder="e.g. getting-started-with-listings" value={form.slug}
              onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} />
          </label>
          <label className={s.formLabel}>
            Cover Image URL
            <input className={s.input} placeholder="https://..." value={form.cover_image_url}
              onChange={e => setForm(f => ({ ...f, cover_image_url: e.target.value }))} />
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Content URL / Reference *
            <input className={s.input} placeholder="Markdown/content location for this guide" value={form.url}
              onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Excerpt (short summary shown on cards)
            <input className={s.input} value={form.excerpt}
              onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} />
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Description *
            <textarea className={s.textarea} rows={4} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </label>
          <label className={s.checkboxLabel}>
            <input type="checkbox" checked={form.isPublished}
              onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))} />
            Published (visible to users)
          </label>
        </div>
        {formError && <p className={s.error}>{formError}</p>}
        <div className={s.formActions}>
          <button className={s.btnSave} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingId !== null ? 'Update' : 'Add Guide'}
          </button>
          {editingId !== null && (
            <button className={s.btnCancel} onClick={handleCancel}>Cancel</button>
          )}
        </div>
      </section>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <section className={s.panel}>
        <h3 className={s.panelTitle}>All Guides ({guides.length})</h3>
        {loadingGuides ? <p className={s.loading}>Loading…</p> : (
          <div className={s.tableWrapper}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Slug</th>
                  <th>Published</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {guides.map(guide => (
                  <tr key={guide._id}>
                    <td>{guide.title}</td>
                    <td>{guide.category ?? '—'}</td>
                    <td>{guide.slug ?? '—'}</td>
                    <td>
                      <button
                        className={guide.isPublished ? s.badgeActive : s.badgeInactive}
                        onClick={() => handleTogglePublished(guide)}
                        title="Toggle published"
                      >
                        {guide.isPublished ? 'Published' : 'Draft'}
                      </button>
                    </td>
                    <td className={s.actions}>
                      <button className={s.btnEdit} onClick={() => handleEdit(guide)}>Edit</button>
                      <button className={s.btnDelete} onClick={() => handleDelete(guide._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {guides.length === 0 && (
                  <tr><td colSpan={5} className={s.empty}>No guides yet. Add one above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminGuides;
